import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaRupeeSign,
  FaExclamationTriangle,
  FaInfoCircle,
  FaHistory,
  FaWallet,
  FaCreditCard,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import DriverLayout from "../../layouts/DriverLayout";
import CommissionPaymentModal from "../../components/Driver/CommissionPaymentModal";
import {
  getDriverWallet,
  payCommission
} from "../../features/wallet/walletSlice";

const WalletCommission = () => {
  const dispatch = useDispatch();
  const { wallet: walletData, isLoading: loading, isError: error } = useSelector((state) => state.wallet);
  const { driver } = useSelector((state) => state.driverAuth);
  
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [commissionTransactions, setCommissionTransactions] = useState([]);

  useEffect(() => {
    if (driver) {
      dispatch(getDriverWallet(driver._id));
    }
  }, [dispatch, driver]);

  useEffect(() => {
    if (walletData?.transactions) {
      // Filter commission-related transactions (updated with new transaction types)
      const commissionTxns = walletData.transactions.filter(
        (txn) => txn.type === 'commission_cut' ||
                 txn.type === 'commission_deduction' ||
                 txn.type === 'admin_commission_deduction' ||
                 txn.type === 'vendor_commission_deduction' ||
                 txn.type === 'commission_payment' ||
                 txn.type === 'cod_ride_deduction' ||
                 txn.type === 'cod_commission_deduction' ||
                 txn.type === 'cod_admin_commission_deduction' ||
                 txn.type === 'cod_vendor_commission_deduction' ||
                 txn.type === 'cod_commission_settlement'
      );
      setCommissionTransactions(commissionTxns);
    }
  }, [walletData]);

  // Smart balance calculation - same logic as WalletCard
  const { bookingHistory } = useSelector((state) => state.driver);

  // Calculate commission breakdown from wallet transactions
  const calculateCommissionBreakdown = () => {
    if (!walletData?.transactions) return { onlineEarnings: 0, pendingCommission: 0, netBalance: 0 };

    let onlineEarnings = 0;
    let pendingCommission = 0;

    walletData.transactions.forEach(transaction => {
      if (transaction.type === 'online_ride_earning') {
        onlineEarnings += transaction.amount;
      } else if (transaction.type === 'cod_commission_deduction') {
        pendingCommission += transaction.amount;
      }
    });

    const netBalance = onlineEarnings - pendingCommission;

    return {
      onlineEarnings,
      pendingCommission,
      netBalance
    };
  };

  const { onlineEarnings, pendingCommission, netBalance } = calculateCommissionBreakdown();
  const currentBalance = walletData?.balance || 0;
  const commissionDue = currentBalance < 0 ? Math.abs(currentBalance) : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handlePayCommission = async (paymentData) => {
    try {
      const result = await dispatch(payCommission({
        driverId: driver._id,
        ...paymentData
      }));

      if (payCommission.fulfilled.match(result)) {
        toast.success("Commission payment submitted successfully! Verification in progress.");
        setShowPaymentModal(false);
        
        // Refresh wallet data
        dispatch(getDriverWallet(driver._id));
      } else {
        toast.error(result.payload?.message || "Failed to submit payment");
      }
    } catch (error) {
      toast.error("Error submitting payment");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading && !walletData) {
    return (
      <DriverLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Link 
              to="/driver/dashboard" 
              className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <FaArrowLeft className="w-5 h-5 text-gray-600" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Commission Management</h1>
              <p className="text-gray-600">Manage your commission payments and history</p>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Link 
            to="/driver/wallet/history" 
            className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow border"
          >
            <div className="flex items-center">
              <FaHistory className="w-6 h-6 text-blue-600 mr-3" />
              <div>
                <h3 className="font-medium text-gray-900">Wallet History</h3>
                <p className="text-sm text-gray-600">View all transactions</p>
              </div>
            </div>
          </Link>
          
          <Link 
            to="/driver/wallet/withdraw" 
            className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow border"
          >
            <div className="flex items-center">
              <FaWallet className="w-6 h-6 text-green-600 mr-3" />
              <div>
                <h3 className="font-medium text-gray-900">Withdraw Funds</h3>
                <p className="text-sm text-gray-600">Request withdrawal</p>
              </div>
            </div>
          </Link>

          <div className="bg-primary/10 p-4 rounded-lg border border-primary/20">
            <div className="flex items-center">
              <FaCreditCard className="w-6 h-6 text-primary mr-3" />
              <div>
                <h3 className="font-medium text-gray-900">Commission</h3>
                <p className="text-sm text-gray-600">Current page</p>
              </div>
            </div>
          </div>
        </div>

        {/* Commission Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Current Balance Card */}
          <div className="bg-white rounded-lg shadow-md p-6 border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Current Wallet Balance</h2>
              <FaWallet className="w-6 h-6 text-gray-400" />
            </div>
            <div className="mb-4">
              <p className={`text-3xl font-bold ${currentBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(currentBalance)}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {currentBalance >= 0 ? 'Available for withdrawal' : 'Commission due to company'}
              </p>

              {/* Commission breakdown as per requirements */}
              <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Online Earnings:</span>
                    <span className="font-medium text-green-600">{formatCurrency(onlineEarnings)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pending Commission (COD):</span>
                    <span className="font-medium text-red-600">-{formatCurrency(pendingCommission)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-medium text-gray-800">Net Available Balance:</span>
                    <span className={`font-bold ${netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(netBalance)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Sync Balance Button */}
              {false && (
                <button
                  onClick={async () => {
                    try {
                      // Import API URL
                      const { API_URL } = await import('../../config/apiConfig');

                      // Get driver token
                      const token = localStorage.getItem('driverToken') ||
                                    localStorage.getItem('token');

                      // Call API to sync wallet balance
                      const response = await fetch(`${API_URL}/wallet/driver/${driver._id}/sync-balance`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                          calculatedBalance: currentBalance,
                          totalEarnings: walletData?.totalEarnings || 0,
                          commissionPaid: walletData?.totalCommissionPaid || 0
                        })
                      });

                      if (response.ok) {
                        // Refresh wallet data
                        dispatch(getDriverWallet(driver._id));
                        toast.success('Wallet balance synced successfully!');
                      } else {
                        toast.error('Failed to sync balance');
                      }
                    } catch (error) {
                      console.error('Failed to sync balance:', error);
                      toast.error('Error syncing balance');
                    }
                  }}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg font-medium transition-colors mt-3"
                >
                  Sync Database Balance
                </button>
              )}
            </div>
            {currentBalance < 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center">
                  <FaExclamationTriangle className="w-4 h-4 text-red-600 mr-2" />
                  <span className="text-sm text-red-800 font-medium">Action Required</span>
                </div>
                <p className="text-sm text-red-700 mt-1">
                  Please pay the outstanding commission to continue receiving rides
                </p>
              </div>
            )}
          </div>

          {/* Commission Due Card */}
          <div className="bg-white rounded-lg shadow-md p-6 border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Commission Due</h2>
              <FaRupeeSign className="w-6 h-6 text-red-500" />
            </div>
            <div className="mb-4">
              <p className={`text-3xl font-bold ${commissionDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(commissionDue)}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {commissionDue > 0 ? 'Amount owed to company' : 'No outstanding commission'}
              </p>
            </div>
            {commissionDue > 0 ? (
              <button
                onClick={() => setShowPaymentModal(true)}
                className="w-full bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Pay Commission Now
              </button>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center">
                  <FaInfoCircle className="w-4 h-4 text-green-600 mr-2" />
                  <span className="text-sm text-green-800 font-medium">All Clear!</span>
                </div>
                <p className="text-sm text-green-700 mt-1">
                  No outstanding commission payments required
                </p>
              </div>
            )}
          </div>
        </div>

        {/* How Commission Works */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">How Commission Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
            <div>
              <h4 className="font-medium mb-2">For Online Payments:</h4>
              <ul className="space-y-1">
                <li>• Commission deducted automatically</li>
                <li>• No driver action required</li>
                <li>• Instant processing</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">For Cash (COD) Payments:</h4>
              <ul className="space-y-1">
                <li>• Driver collects full amount from passenger</li>
                <li>• Commission owed to company</li>
                <li>• Pay through this page when convenient</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Recent Commission Transactions */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Recent Commission Transactions</h2>
            <p className="text-gray-600 text-sm">Last 10 commission-related transactions</p>
          </div>
          
          {commissionTransactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {commissionTransactions.slice(0, 10).map((transaction, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(transaction.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {transaction.description}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                        transaction.amount < 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {formatCurrency(transaction.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          transaction.amount < 0 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {transaction.amount < 0 ? 'Deduction' : 'Payment'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <FaRupeeSign className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Commission Transactions</h3>
              <p className="text-gray-600">Your commission transaction history will appear here</p>
            </div>
          )}
          
          {commissionTransactions.length > 10 && (
            <div className="p-4 border-t bg-gray-50 text-center">
              <Link 
                to="/driver/wallet/history"
                className="text-primary hover:text-primary-dark font-medium"
              >
                View All Transactions →
              </Link>
            </div>
          )}
        </div>

        {/* Commission Payment Modal */}
        <CommissionPaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSubmit={handlePayCommission}
          commissionAmount={commissionDue}
        />
      </div>
    </DriverLayout>
  );
};

export default WalletCommission;
